import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, UserIcon, ArrowRight } from 'lucide-react';
import { Button } from './Button';
import { useModal } from './ModalContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
export const Header = ({
  isStatic = false
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogo, setShowLogo] = useState(isStatic);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const {
    openSignupModal,
    openTalkToUs
  } = useModal();
  useEffect(() => {
    const handleScroll = () => {
      // If header is static, always show logo regardless of scroll position
      if (!isStatic) {
        const heroLogo = document.getElementById('hero-logo');
        if (heroLogo) {
          const rect = heroLogo.getBoundingClientRect();
          setShowLogo(rect.bottom < 0);
        }
      }
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isStatic]);
  // Handle navigation with proper scrolling
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    if (anchor === 'cta') {
      openTalkToUs();
      if (isMenuOpen) setIsMenuOpen(false);
      return;
    }
    if (isHomePage) {
      // If already on home page, just scroll to the section
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      // If not on home page, navigate to home and then scroll to section
      navigate('/');
      // Need to wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    // Close mobile menu if open
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };
  // Create links with proper routing and handling
  const navItems = [{
    name: 'What Is Nisa',
    anchor: 'what-is-nisa'
  }, {
    name: 'Why Nisa Works',
    anchor: 'why-nisa-works'
  }];
  return <>
      {/* Desktop Header */}
      <header className={`sticky top-0 z-40 w-full transition-all duration-300 md:block hidden ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-gray-100/20 shadow-sm' : 'bg-transparent'}`} data-id="element-135" data-mp="18lih8p3qx7hc">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between relative" data-id="element-136" data-mp="yqqmru5gn0qs">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-50/50 via-accent-500/10 to-blue-50/50 opacity-50 -z-10" data-id="element-137" data-mp="13o8slbo6kzuk" />
          <div className="flex items-center relative z-10" data-id="element-138" data-mp="2y1ro6fyni8d8">
            <a href="/" className={`h-8 transition-all duration-300 ${showLogo || isStatic ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`} data-id="element-139" data-mp="2y18grt6mn2dg">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-full w-auto" data-id="element-140" data-mp="hdh0vdzceuiz" />
            </a>
          </div>
          <nav className="flex items-center space-x-8" data-id="element-141" data-mp="1544l8yqg3cmf">
            <div className="flex items-center space-x-8" data-id="element-142" data-mp="106326dt0rday">
              {navItems.map((item, index) => <a key={index} href={`#${item.anchor}`} onClick={e => handleNavigation(e, item.anchor)} className="text-gray-600 hover:text-gray-900 transition-colors duration-300" data-id="element-143" data-mp="1f3253ocxehz3" data-mpi={index}>
                  {item.name}
                </a>)}
              <Link to="/schools" className="text-gray-600 hover:text-gray-900 transition-colors duration-300" data-id="element-144" data-mp="3bwx8h837hcx6">
                Schools
              </Link>
              <a href="#cta" onClick={e => handleNavigation(e, 'cta')} className="text-gray-600 hover:text-gray-900 transition-colors duration-300" data-id="element-145" data-mp="27wgq0an95hcg">
                Request Demo
              </a>
            </div>
            <div className="flex items-center space-x-4" data-id="element-146" data-mp="1k8yx2bs94d7w">
              <a href="https://app.nisa.coach/" data-id="element-147" data-mp="1k6cqaed6q1qq">
                <Button variant="outline" size="sm" data-id="element-148" data-mp="1p8bt9n50iwn5">
                  Log in / Sign up
                </Button>
              </a>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Floating Menu Button */}
      <button className="md:hidden fixed top-6 right-6 z-50 w-12 h-12 flex items-center justify-center rounded-xl bg-white/50 backdrop-blur-md shadow-lg transition-all duration-300 hover:bg-white/60" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-149" data-mp="1y0vo0ci6oh2p">
        <div className="relative flex items-center justify-center w-6 h-6" data-id="element-150" data-mp="xxo305q8xs3e">
          <span className={`absolute w-6 h-0.5 bg-gray-600 transform transition-all duration-300 ${isMenuOpen ? 'rotate-45' : '-translate-y-2'}`} data-id="element-151" data-mp="t1ywtjjq1cy4" />
          <span className={`absolute w-6 h-0.5 bg-gray-600 transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} data-id="element-152" data-mp="k0sgbah2o24v" />
          <span className={`absolute w-6 h-0.5 bg-gray-600 transform transition-all duration-300 ${isMenuOpen ? '-rotate-45' : 'translate-y-2'}`} data-id="element-153" data-mp="f2qystzaoavi" />
        </div>
      </button>

      {/* Mobile Navigation Menu */}
      <div className={`md:hidden fixed inset-0 backdrop-blur-lg bg-white/95 transition-all duration-500 ease-in-out ${isMenuOpen ? 'opacity-100 pointer-events-auto translate-x-0' : 'opacity-0 pointer-events-none translate-x-full'}`} style={{
      zIndex: 40
    }} data-id="element-154" data-mp="1a184c8tmhs2">
        <div className="container mx-auto px-6 py-24" data-id="element-155" data-mp="3dskfnpqymmsv">
          <nav className="flex flex-col" data-id="element-156" data-mp="3dv6pxx58jges">
            <div className="space-y-6" data-id="element-157" data-mp="3isgn5yyv8wd6">
              {navItems.map((item, index) => <a key={index} href={`#${item.anchor}`} onClick={e => handleNavigation(e, item.anchor)} className="block text-xl text-gray-800 hover:text-primary-500 transition-all duration-300 transform hover:translate-x-2 py-2 px-4 rounded-lg hover:bg-primary-50 active:bg-primary-100" data-id="element-158" data-mp="33vftvr110tcl" data-mpi={index}>
                  {item.name}
                </a>)}
              <Link to="/schools" onClick={() => setIsMenuOpen(false)} className="block text-xl text-gray-800 hover:text-primary-500 transition-all duration-300 transform hover:translate-x-2 py-2 px-4 rounded-lg hover:bg-primary-50 active:bg-primary-100" data-id="element-159" data-mp="3mt8u2lk9mp6a">
                For Schools
              </Link>
              <a href="#cta" onClick={e => handleNavigation(e, 'cta')} className="block text-xl text-gray-800 hover:text-primary-500 transition-all duration-300 transform hover:translate-x-2 py-2 px-4 rounded-lg hover:bg-primary-50 active:bg-primary-100" data-id="element-160" data-mp="28tf7ott97t7t">
                Request Demo
              </a>
            </div>
            <div className="mt-12 space-y-4" data-id="element-161" data-mp="1put51ai39u1s">
              <a href="https://app.nisa.coach/" className="block" data-id="element-162" data-mp="1pvl3ct32m5vr">
                <Button variant="outline" fullWidth size="lg" data-id="element-163" data-mp="1kx9t4h8v5td0">
                  Log in / Sign up
                </Button>
              </a>
            </div>
          </nav>
        </div>
      </div>
    </>;
};